import { useMemo } from 'react';

import isNil from 'lodash/isNil';

import { TileRate, TileRateProvision } from '@nocowanie/noc-api';

import { dateHelpers, formatHelpers } from '@nocowanie/core/helpers';

import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile';
import { ACCOMMODATION_TILE } from '@app/consts/listing.consts';

export const useAccommodationRatePlanFeatures = (
    t: AccommodationTileProps['t'],
    rate?: TileRate,
) => {
    const features = useMemo(() => {
        if (isNil(rate)) {
            return null;
        }

        const { cancellationPolicy, prepayment, provision } = rate;
        const isCancellationFree = cancellationPolicy?.freeCancellation;
        const isCancellationNonRefundable = cancellationPolicy?.notRefundable;
        const features = [];
        let mobileFeaturesCounter = 0;

        if (provision && provision.type !== TileRateProvision.type.NO_MEAL) {
            const shouldShowOnMobile =
                mobileFeaturesCounter < ACCOMMODATION_TILE.MAX_MOBILE_RATE_PLAN_FEATURES;

            features.push({
                key: 'provision',
                dataTestId: 'acc-tile-provision',
                color: 'green.950',
                content: t('ratePlan.provisionIncluded', {
                    provision:
                        provision.type === TileRateProvision.type.HALF_BOARD
                            ? t('ratePlan.provisionHalfBoardLabel')
                            : provision.name,
                }),
                showOnMobile: shouldShowOnMobile,
            });

            if (shouldShowOnMobile) {
                mobileFeaturesCounter++;
            }
        }

        if (isCancellationFree || isCancellationNonRefundable) {
            const cancellationText = () => {
                if (isCancellationFree) {
                    if (cancellationPolicy.cancellationDateTo) {
                        return t('ratePlan.cancellationPolicies.freeUntil', {
                            date: dateHelpers
                                .format(new Date(cancellationPolicy.cancellationDateTo), 'd MMM')
                                .toLowerCase(),
                        });
                    }

                    return t('ratePlan.cancellationPolicies.free');
                } else if (isCancellationNonRefundable) {
                    return t('ratePlan.cancellationPolicies.nonRefundable');
                }

                return null;
            };

            const text = cancellationText();

            if (text) {
                const shouldShowOnMobile =
                    isCancellationFree &&
                    mobileFeaturesCounter < ACCOMMODATION_TILE.MAX_MOBILE_RATE_PLAN_FEATURES;

                features.push({
                    key: 'cancellation',
                    dataTestId: 'acc-tile-cancellation',
                    color: isCancellationFree ? 'green.950' : undefined,
                    fontWeight: isCancellationFree ? 'bold' : undefined,
                    showOnMobile: shouldShowOnMobile,
                    content: text,
                });

                if (shouldShowOnMobile) {
                    mobileFeaturesCounter++;
                }
            }
        }

        if (prepayment) {
            const noPrepayment = prepayment.rate === 0;
            const shouldShowOnMobile =
                noPrepayment &&
                mobileFeaturesCounter < ACCOMMODATION_TILE.MAX_MOBILE_RATE_PLAN_FEATURES;
            const prepaymentText = () => {
                if (noPrepayment) {
                    return t('ratePlan.prepayment.noPrepayment');
                }
                if (prepayment.fullPrepayment) {
                    return t('ratePlan.prepayment.fullPrepayment');
                }
                if (typeof prepayment.convertedRate === 'number') {
                    return t('ratePlan.prepayment.partialPrepayment', {
                        prepayment: formatHelpers.price(
                            prepayment.convertedRate,
                            rate.convertedCurrency,
                            false,
                            true,
                        ),
                    });
                }
            };

            features.push({
                key: 'prepayment',
                dataTestId: 'acc-tile-prepayment',
                content: prepaymentText(),
                showOnMobile: shouldShowOnMobile,
                color: noPrepayment ? 'green.950' : undefined,
                fontWeight: noPrepayment ? 'bold' : undefined,
            });

            if (shouldShowOnMobile) {
                mobileFeaturesCounter++;
            }
        }

        return features;
    }, [rate, t]);

    return features;
};
